import React, { useState, useEffect } from "react";
import InputField from "../../components/InputComponent/InputField";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
// import Calendar from "react-calendar";
import axiosInstance from "../../axiosInstance";
import "react-calendar/dist/Calendar.css"; // Import Calendar CSS
import SelectInputField from "../../components/InputComponent/SelectInputField";
import { toast } from "react-toastify"; // Import Toastify
import "./TeamLeaveApply.scss";
import { useAuth } from "../../contexts/AuthContext";

const TeamLeave = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [submissionError, setSubmissionError] = useState("");
  const [tableData, setTableData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDates, setSelectedDates] = useState([null, null]);
  const [optionalHolidays, setOptionalHolidays] = useState([]);
  const [selectedOptionalHoliday, setSelectedOptionalHoliday] = useState("");
  const [isOptionalLeave, setIsOptionalLeave] = useState(false);
  const [fallbackEmployees, setFallbackEmployees] = useState([]);
  const [selectedRoleView, setSelectedRoleView] = useState(""); // Track selected role view
  const [userRoles, setUserRoles] = useState([]);

  const ROLE_LABELS = {
    ADMIN: "Admin",
    MANAGER: "Manager",
    HR: "HR",
  };

  const { hasRole } = useAuth(); // Add this line

  const [formData, setFormData] = useState({
    leaveType: "",
    leaveTypeId: null,
    dateRange: [null, null],
    duration: "full",
    reason: "",
    fallback: "",
    responsibility: "",
    employee: "",
  });

  const [errors, setErrors] = useState({});
  const [suggestions, setSuggestions] = useState([]);

  // Helper functions for date handling (similar to ApplyLeave)
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateForInput = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const calculateLeaveDays = () => {
    if (!formData.dateRange[0] || !formData.dateRange[1]) return 0;

    const startDate = new Date(formData.dateRange[0]);
    const endDate = new Date(formData.dateRange[1]);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const timeDiff = endDate - startDate;
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

    return daysDiff;
  };

  const handleStartDateChange = (e) => {
    const startDate = new Date(e.target.value);
    const endDate = formData.dateRange[1];

    if (endDate && startDate > endDate) {
      setFormData({
        ...formData,
        dateRange: [startDate, startDate],
      });
      setSelectedDates([startDate, startDate]);
    } else {
      setFormData({
        ...formData,
        dateRange: [startDate, endDate],
      });
      setSelectedDates([startDate, endDate]);
    }
  };

  const handleEndDateChange = (e) => {
    const endDate = new Date(e.target.value);
    const startDate = formData.dateRange[0] || endDate;

    setFormData({
      ...formData,
      dateRange: [startDate, endDate],
    });
    setSelectedDates([startDate, endDate]);
  };

  useEffect(() => {
    // Determine user's roles and set default view
    const roles = [];
    if (hasRole("ROLE_ADMIN")) roles.push("ADMIN");
    if (hasRole("ROLE_MANAGER")) roles.push("MANAGER");
    if (hasRole("ROLE_HR")) roles.push("HR");

    setUserRoles(roles);

    // Set default selected role view (first available role)
    if (roles.length > 0) {
      setSelectedRoleView(roles[0]);
    }
  }, [hasRole]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (!selectedRoleView) return;

      try {
        let apiUrl;

        // Determine API endpoint based on selected role view
        if (selectedRoleView === "ADMIN") {
          apiUrl = `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/All`;
        } else if (selectedRoleView === "MANAGER") {
          apiUrl = `${process.env.REACT_APP_API_MANAGER_URL}/employees/manager`;
        } else if (selectedRoleView === "HR") {
          apiUrl = `${process.env.REACT_APP_API_HR_URL}/employees/hr`;
        }

        const response = await axiosInstance.get(apiUrl);
        setEmployees(response.data.data);
        setFallbackEmployees(response.data.data.map((emp) => emp.firstName));
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("No employees found!");
        setEmployees([]);
        setFallbackEmployees([]);
      }
    };

    fetchEmployees();
  }, [selectedRoleView]);

  useEffect(() => {
    const fetchLeaveType = async () => {
      if (!formData.employee) {
        setLeaveTypes([]);
        setTableData([]);
        return;
      }

      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/Leave-Types/employee/${formData.employee}`,
        );

        const data = response.data.data;

        if (Array.isArray(data)) {
          setLeaveTypes(data);

          if (data.length > 0) {
            const firstLeaveType = data[0];
            setFormData((prev) => ({
              ...prev,
              leaveType: firstLeaveType.leaveTypeName,
              leaveTypeId: firstLeaveType.leaveTypeId,
            }));
            setIsOptionalLeave(firstLeaveType.optionalHoliday || false);
          } else {
            setFormData((prev) => ({
              ...prev,
              leaveType: "",
              leaveTypeId: null,
            }));
            setIsOptionalLeave(false);
          }

          const updatedTableData = data.map((leave) => ({
            category: leave.leaveTypeName,
            totalLeaves: leave.totalLeaves,
            availableLeaves: leave.availableLeaves,
            usedLeaves: leave.totalLeaves - leave.availableLeaves,
            remainingLeaves: leave.availableLeaves,
          }));

          setTableData(updatedTableData);
        } else {
          console.error("Unexpected response format:", response.data);
          setLeaveTypes([]);
          setTableData([]);
        }
      } catch (error) {
        console.error("Error fetching leave types for employee:", error);
        setLeaveTypes([]);
        setTableData([]);

        if (error.response?.status === 404) {
          toast.error("No leave types found for selected employee");
        } else {
          toast.error("Error fetching leave types. Please try again.");
        }
      }
    };

    fetchLeaveType();
  }, [formData.employee]);

  useEffect(() => {
    const fetchOptionalHolidays = async () => {
      if (isOptionalLeave) {
        try {
          const response = await axiosInstance.get(
            `${process.env.REACT_APP_API_EMPLOYEE_URL}/holidays/all`,
          );
          const { status, data } = response.data;

          if (status === 200 && Array.isArray(data)) {
            // Filter only OPTIONAL holidays
            const optionalHolidaysOnly = data.filter(
              (holiday) => holiday.holidayType?.toUpperCase() === "OPTIONAL",
            );
            setOptionalHolidays(optionalHolidaysOnly);
          } else {
            console.error(
              "Unexpected response format for optional holidays:",
              response.data,
            );
            setOptionalHolidays([]);
          }
        } catch (error) {
          console.error("Error fetching optional holidays:", error);
          setOptionalHolidays([]);
        }
      }
    };

    fetchOptionalHolidays();
  }, [isOptionalLeave]);

  const predefinedReasons = [
    "Family Emergency",
    "Personal Work",
    "Vacation",
    "I am sick!",
    "Doctor's Appointment",
    "Child Care",
    "Car Trouble",
    "Weather Issues",
    "Bereavement",
    "Mental Health Day",
    "Relocation",
    "Unexpected Travel",
    "Public Transportation Delays",
    "Study or Exam",
    "Pet Emergency",
    "Meeting with Authorities",
    "Health Check-up",
  ];

  const handleRoleViewChange = (role) => {
    setSelectedRoleView(role);

    setFormData({
      leaveType: "",
      leaveTypeId: null,
      dateRange: [null, null],
      duration: "full",
      reason: "",
      fallback: "",
      responsibility: "",
      employee: "", // ← employee RESET
    });

    setLeaveTypes([]); // ← CLEARED
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "employee") {
      setFormData({
        ...formData,
        [name]: parseInt(value, 10) || "",
        leaveType: "",
        leaveTypeId: null,
        dateRange: [null, null],
        duration: "full",
        reason: "",
        fallback: "",
        responsibility: "",
      });
      setSelectedDates([null, null]);
      setSelectedOptionalHoliday("");
      setIsOptionalLeave(false);
      setErrors({});
    } else {
      setFormData({
        ...formData,
        [name]: name === "fallback" ? parseInt(value, 10) : value,
      });
    }
  };

  const handleReasonChange = (e) => {
    setFormData({ ...formData, reason: e.target.value });

    const value = e.target.value;
    const filteredSuggestions = predefinedReasons.filter((reason) =>
      reason.toLowerCase().includes(value.toLowerCase()),
    );
    setSuggestions(filteredSuggestions);
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData({
      ...formData,
      reason: suggestion,
    });
    setSuggestions([]);
  };

  const handleButtonClick = (name, value) => {
    if (name === "leaveType") {
      const selectedLeaveType = leaveTypes.find(
        (type) => type.leaveTypeName === value,
      );

      const isOptional = selectedLeaveType?.optionalHoliday || false;

      setFormData({
        ...formData,
        leaveType: value,
        leaveTypeId: selectedLeaveType ? selectedLeaveType.leaveTypeId : null,
      });

      setIsOptionalLeave(isOptional);

      if (!isOptional) {
        setSelectedOptionalHoliday("");
      }

      if (isOptional) {
        setSelectedDates([null, null]);
        setFormData((prev) => ({
          ...prev,
          dateRange: [null, null],
        }));
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleCalendarChange = (date) => {
    const [start, end] = date;
    setFormData({
      ...formData,
      dateRange: [start, end || start],
    });
  };

  const handleOptionalHolidayChange = (e) => {
    const holidayId = e.target.value;
    setSelectedOptionalHoliday(holidayId);

    if (holidayId) {
      const selectedHoliday = optionalHolidays.find(
        (holiday) => holiday.id.toString() === holidayId, // FIXED
      );

      if (selectedHoliday) {
        const holidayDate = new Date(selectedHoliday.eventDate);
        setFormData((prev) => ({
          ...prev,
          dateRange: [holidayDate, holidayDate],
        }));
        setSelectedDates([holidayDate, holidayDate]);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.employee)
      newErrors.employee = "Employee selection is required.";
    if (!formData.leaveType) newErrors.leaveType = "Leave Type is required.";
    if (!formData.leaveTypeId)
      newErrors.leaveTypeId = "Invalid Leave Type selected.";
    if (!formData.duration) newErrors.duration = "Duration is required.";
    if (!formData.reason?.trim()) newErrors.reason = "Reason is required.";

    if (isOptionalLeave) {
      if (!selectedOptionalHoliday) {
        newErrors.optionalHoliday = "Please select an optional holiday.";
      }
    } else {
      const [startDate, endDate] = formData.dateRange;
      if (
        !startDate ||
        !endDate ||
        isNaN(new Date(startDate)) ||
        isNaN(new Date(endDate))
      ) {
        newErrors.dateRange = "Valid From and To dates are required.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const leaveDates = [];

      const startDate = new Date(formData.dateRange[0]);
      const endDate = new Date(formData.dateRange[1]);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const timeDiff = endDate - startDate;
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

      let noOfDays;
      if (formData.duration === "full") {
        noOfDays = daysDiff;
      } else {
        noOfDays = daysDiff * 0.5;
      }

      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, "0");
        const day = String(currentDate.getDate()).padStart(2, "0");
        leaveDates.push(`${year}-${month}-${day}`);

        currentDate.setDate(currentDate.getDate() + 1);
      }

      const leaveDurationEnumMap = {
        full: "FULL_DAY",
        first_half: "FIRST_HALF",
        second_half: "SECOND_HALF",
      };

      const selectedEmployeeId = formData.employee;
      const fallbackEmployeeId = formData.fallback
        ? parseInt(formData.fallback, 10)
        : null;

      if (!selectedEmployeeId) {
        setSubmissionError("Please select an employee.");
        return;
      }

      const requestBody = {
        employeeId: selectedEmployeeId,
        leaveId: 0,
        leaveDuration: leaveDurationEnumMap[formData.duration],
        noOfDays: noOfDays,
        comments: formData.reason,
        responsibility: formData.responsibility,
        leaveDates: leaveDates,
        fallBackEmployeeId: fallbackEmployeeId || 0,
        leaveTypeId: formData.leaveTypeId,
      };

      try {
        let apiEndpoint;

        if (hasRole("ROLE_ADMIN")) {
          apiEndpoint = `${process.env.REACT_APP_API_BASE_URL}/leave-history/Team`;
        } else if (hasRole("ROLE_MANAGER")) {
          apiEndpoint = `${process.env.REACT_APP_API_MANAGER_URL}/leave-history/Team`;
        } else if (hasRole("ROLE_HR")) {
          apiEndpoint = `${process.env.REACT_APP_API_HR_URL}/leave-history/Team`;
        } else {
          apiEndpoint = `${process.env.REACT_APP_API_BASE_URL}/leave-history/Team`;
        }

        const response = await axiosInstance.post(apiEndpoint, requestBody);

        setSubmissionError("");
        toast.success("Leave request submitted successfully!");

        setFormData({
          leaveType: "",
          leaveTypeId: null,
          dateRange: [null, null],
          duration: "full",
          reason: "",
          fallback: "",
          responsibility: "",
          employee: "",
        });
        setSelectedDates([null, null]);
        setSelectedOptionalHoliday("");
        setIsOptionalLeave(false);
        setErrors({});
        setLeaveTypes([]);
        setTableData([]);
      } catch (error) {
        console.error("Error submitting leave request:", error);
        console.error("Request Body on Error:", requestBody);

        if (error.response) {
          console.error("Error Response Data:", error.response.data);
          console.error("Error Response Status:", error.response.status);
          let errorMessage;

          switch (error.response.status) {
            case 400:
              errorMessage = "Bad Request. Please check your input.";
              break;
            case 401:
              errorMessage = "Unauthorized. Please log in again.";
              break;
            case 403:
              errorMessage =
                "Forbidden. You don't have permission to perform this action.";
              break;
            case 404:
              errorMessage =
                "Not Found. The requested resource could not be found.";
              break;
            case 409:
              errorMessage =
                "Conflict - Leave may already exist for these dates.";
              break;
            case 500:
              errorMessage = "Server Error. Please try again later.";
              break;
            default:
              errorMessage =
                "Failed to submit leave request. Please try again.";
          }
          setSubmissionError(errorMessage);
        } else {
          setSubmissionError(
            "Network error. Please check your connection and try again.",
          );
        }
      }
    }
  };

  const Calendar = ({ onDateSelect, selectedDates, onClose }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [hoveredDate, setHoveredDate] = useState(null);
    const [selectionStep, setSelectionStep] = useState(
      selectedDates[0] && selectedDates[1]
        ? "complete"
        : selectedDates[0]
          ? "selecting-end"
          : "selecting-start",
    );

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const getDaysInMonth = (date) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const isDateSelected = (day) => {
      const checkDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      return selectedDates.some(
        (date) =>
          date &&
          date.getDate() === checkDate.getDate() &&
          date.getMonth() === checkDate.getMonth() &&
          date.getFullYear() === checkDate.getFullYear(),
      );
    };

    const isDateInRange = (day) => {
      if (!selectedDates[0] || !selectedDates[1]) {
        if (selectedDates[0] && hoveredDate) {
          const checkDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            day,
          );
          const hoverDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            hoveredDate,
          );
          const start =
            selectedDates[0] < hoverDate ? selectedDates[0] : hoverDate;
          const end =
            selectedDates[0] < hoverDate ? hoverDate : selectedDates[0];
          return checkDate >= start && checkDate <= end;
        }
        return false;
      }
      const checkDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      return checkDate >= selectedDates[0] && checkDate <= selectedDates[1];
    };

    const isToday = (day) => {
      const today = new Date();
      const checkDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      return checkDate.toDateString() === today.toDateString();
    };

    const isPastDate = (day) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      return checkDate < today;
    };

    const handleDateClick = (day) => {
      const selectedDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );

      // Removed the isPastDate check - users can now select any date

      if (selectionStep === "selecting-start" || selectionStep === "complete") {
        onDateSelect(selectedDate, "start");
        setSelectionStep("selecting-end");
      } else if (selectionStep === "selecting-end") {
        onDateSelect(selectedDate, "end");
        setSelectionStep("complete");
      }
    };

    const clearSelection = () => {
      onDateSelect(null, "clear");
      setSelectionStep("selecting-start");
    };

    const navigateMonth = (direction) => {
      setCurrentDate((prev) => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() + direction);
        return newDate;
      });
    };

    const renderCalendarDays = () => {
      const daysInMonth = getDaysInMonth(currentDate);
      const firstDay = getFirstDayOfMonth(currentDate);
      const days = [];

      for (let i = 0; i < firstDay; i++) {
        days.push(
          <div key={`empty-${i}`} className="calendar-day empty"></div>,
        );
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const isSelected = isDateSelected(day);
        const isInRange = isDateInRange(day);
        const isTodayDate = isToday(day);
        const isPast = isPastDate(day);

        days.push(
          <div
            key={day}
            className={`calendar-day ${isSelected ? "selected" : ""} ${
              isInRange ? "in-range" : ""
            } ${isTodayDate ? "today" : ""} ${isPast ? "past" : ""}`}
            onClick={() => handleDateClick(day)}
            onMouseEnter={() => setHoveredDate(day)}
            onMouseLeave={() => setHoveredDate(null)}
          >
            {day}
          </div>,
        );
      }

      return days;
    };

    return (
      <div className="calendar-overlay" onClick={onClose}>
        <div
          className="calendar-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="calendar-header">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="nav-button"
            >
              ‹
            </button>
            <h3>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="nav-button"
            >
              ›
            </button>
          </div>

          <div className="calendar-status">
            {selectionStep === "selecting-start" && (
              <p className="calender-status-text">Select start date</p>
            )}
            {selectionStep === "selecting-end" && (
              <p className="calender-status-text">Select end date</p>
            )}
            {selectionStep === "complete" && (
              <p className="calender-status-text">Date range selected</p>
            )}
          </div>

          <div className="calendar-weekdays">
            {daysOfWeek.map((day) => (
              <div key={day} className="weekday">
                {day}
              </div>
            ))}
          </div>

          <div className="calendar-grid">{renderCalendarDays()}</div>

          <div className="calendar-footer">
            <ButtonWithIcon
              text="Clear"
              icon="tdesign:close"
              className="cancel-button"
              onClick={clearSelection}
            />

            <ButtonWithIcon
              text="Submit"
              icon="tdesign:check"
              className="submit-button"
              onClick={onClose}
            />
          </div>
        </div>
      </div>
    );
  };

  const handleCalendarDateSelect = (date, type) => {
    if (type === "clear") {
      setSelectedDates([null, null]);
      handleCalendarChange([null, null]);
    } else if (type === "start") {
      setSelectedDates([date, null]);
      handleCalendarChange([date, null]);
    } else if (type === "end") {
      const newRange = [selectedDates[0], date];
      if (newRange[0] > newRange[1]) newRange.reverse();
      setSelectedDates(newRange);
      handleCalendarChange(newRange);
    }
  };

  return (
    <div className="team-leave-apply-container">
      <div className="team-leave-form-section">
        <form onSubmit={handleSubmit}>
          <div className="team-leave-form-content">
            <div className="team-leave-form-fields">
              <div className="team-leave-form-row">
                {/* 30% width for Select Employee */}
                <div className="team-leave-form-group team-leave-employee-select">
                  <div className="team-leave-employee-header">
                    <SelectInputField
                      label="Select Employee"
                      id="employee"
                      name="employee"
                      value={formData.employee}
                      onChange={handleChange}
                      options={[
                        { value: "", label: "Select Employee" },
                        ...employees.map((emp) => ({
                          value: emp.empId,
                          label: `${emp.firstName} ${emp.lastName}`,
                        })),
                      ]}
                    />

                    {/* Show role buttons only if user has more than 1 role */}
                    {userRoles.length > 1 && (
                      <div className="team-leave-role-buttons">
                        <label style={{ fontSize: "12px", fontWeight: 500 }}>
                          Apply as:
                        </label>{" "}
                        {userRoles.map((role) => (
                          <button
                            key={role}
                            type="button"
                            className={`team-leave-role-btn ${
                              selectedRoleView === role ? "active" : ""
                            }`}
                            onClick={() => handleRoleViewChange(role)}
                          >
                            {ROLE_LABELS[role]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.employee && (
                    <span className="team-leave-error-text">
                      {errors.employee}
                    </span>
                  )}
                </div>

                <div className="team-leave-form-group team-leave-date-section">
                  {isOptionalLeave ? (
                    <>
                      <SelectInputField
                        label="Optional holiday"
                        id="optionalHoliday"
                        name="optionalHoliday"
                        value={selectedOptionalHoliday}
                        onChange={handleOptionalHolidayChange}
                        options={[
                          { value: "", label: "Select Optional Holiday" },
                          ...optionalHolidays.map((holiday) => ({
                            value: holiday.id.toString(), // FIXED
                            label: `${holiday.holidayName} - ${new Date(
                              holiday.eventDate,
                            ).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}`,
                          })),
                        ]}
                      />
                      {errors.optionalHoliday && (
                        <span className="team-leave-error-text">
                          {errors.optionalHoliday}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="team-leave-date-range-container">
                        <div className="team-leave-date-field">
                          <label htmlFor="startDate">From Date</label>
                          <div className="team-leave-date-input-container">
                            <input
                              type="date"
                              id="startDate"
                              name="startDate"
                              value={
                                formData.dateRange[0]
                                  ? formatDateForInput(formData.dateRange[0])
                                  : ""
                              }
                              onChange={handleStartDateChange}
                              className="team-leave-date-input"
                              required
                            />
                          </div>
                        </div>

                        <div className="team-leave-date-field">
                          <label htmlFor="endDate">To Date</label>
                          <div className="team-leave-date-input-container">
                            <input
                              type="date"
                              id="endDate"
                              name="endDate"
                              value={
                                formData.dateRange[1]
                                  ? formatDateForInput(formData.dateRange[1])
                                  : ""
                              }
                              onChange={handleEndDateChange}
                              min={
                                formData.dateRange[0]
                                  ? formatDateForInput(formData.dateRange[0])
                                  : ""
                              }
                              className="team-leave-date-input"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      <div className="team-leave-fallback-container">
                        <SelectInputField
                          label="Fallback Employee"
                          id="fallback-employee"
                          name="fallback"
                          value={formData.fallback}
                          onChange={handleChange}
                          options={[
                            {
                              value: "",
                              label: "Select Fallback Employee",
                            },
                            ...employees.map((emp) => ({
                              value: emp.empId,
                              label: `${emp.firstName} ${emp.lastName}`,
                            })),
                          ]}
                        />
                      </div>

                      {/* Display selected date range summary */}
                      {formData.dateRange[0] && formData.dateRange[1] && (
                        <div className="team-leave-date-range-summary">
                          <span className="team-leave-summary-text">
                            📅 {calculateLeaveDays()} day
                            {calculateLeaveDays() !== 1 ? "s" : ""} selected (
                            {formatDate(formData.dateRange[0])} to{" "}
                            {formatDate(formData.dateRange[1])})
                          </span>
                        </div>
                      )}

                      {errors.dateRange && (
                        <span className="team-leave-error-text">
                          {errors.dateRange}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="team-leave-form-row-select">
                <div className="team-leave-type-container">
                  <h3>Leave Type :</h3>
                  <div className="team-leave-button-group">
                    {leaveTypes.map((type) => {
                      const leaveData = tableData.find(
                        (data) => data.category === type.leaveTypeName,
                      );
                      const isDisabled =
                        leaveData && leaveData.remainingLeaves <= 0;

                      return (
                        <button
                          key={type.leaveTypeId}
                          type="button"
                          className={`${
                            formData.leaveType === type.leaveTypeName
                              ? "active"
                              : ""
                          } ${isDisabled ? "disabled" : ""}`}
                          onClick={() =>
                            !isDisabled &&
                            handleButtonClick("leaveType", type.leaveTypeName)
                          }
                          disabled={isDisabled}
                          title={isDisabled ? "No remaining leaves" : ""}
                        >
                          {type.leaveTypeName}
                          {isDisabled && " (0 remaining)"}
                        </button>
                      );
                    })}
                  </div>
                  {errors.leaveType && (
                    <span className="team-leave-error-text">
                      {errors.leaveType}
                    </span>
                  )}

                  {!formData.employee && (
                    <div className="team-leave-info-text">
                      <p>Please select an employee to fetch leave types.</p>
                    </div>
                  )}

                  {formData.employee && leaveTypes.length === 0 && (
                    <div className="team-leave-info-text">
                      <p>No leave types available for selected employee.</p>
                    </div>
                  )}
                </div>

                <div className="team-leave-duration-container">
                  <h3>Duration :</h3>
                  <div className="team-leave-button-group">
                    <button
                      type="button"
                      className={formData.duration === "full" ? "active" : ""}
                      onClick={() => handleButtonClick("duration", "full")}
                    >
                      Full Day
                    </button>
                    <button
                      type="button"
                      className={
                        formData.duration === "first_half" ? "active" : ""
                      }
                      onClick={() =>
                        handleButtonClick("duration", "first_half")
                      }
                    >
                      First Half
                    </button>
                    <button
                      type="button"
                      className={
                        formData.duration === "second_half" ? "active" : ""
                      }
                      onClick={() =>
                        handleButtonClick("duration", "second_half")
                      }
                    >
                      Second Half
                    </button>
                  </div>
                  {errors.duration && (
                    <span className="team-leave-error-text">
                      {errors.duration}
                    </span>
                  )}
                </div>
              </div>

              <div className="team-leave-form-row team-leave-form-row--split">
                <div className="team-leave-form-group">
                  <label>Reason *</label>
                  <div className="team-leave-reason-container">
                    <InputField
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleReasonChange}
                      placeholder="Enter reason for leave or select from predefined reasons below"
                      rows="3"
                    />

                    {/* Predefined reason buttons */}
                    <div className="team-leave-reason-buttons">
                      {predefinedReasons.map((reason, index) => (
                        <button
                          key={index}
                          type="button"
                          className="team-leave-reason-button"
                          onClick={() => handleSuggestionClick(reason)}
                        >
                          {reason}
                        </button>
                      ))}
                    </div>
                  </div>
                  {errors.reason && (
                    <span className="team-leave-error-text">
                      {errors.reason}
                    </span>
                  )}
                </div>
                <div className="team-leave-form-group">
                  <label>Responsibility</label>
                  <textarea
                    id="responsibility"
                    name="responsibility"
                    value={formData.responsibility}
                    onChange={handleChange}
                    placeholder="Describe your responsibilities and handover details"
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Show validation errors if form is invalid */}
          {Object.keys(errors).length > 0 && (
            <div className="team-leave-error">
              <ul>
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          <ButtonWithIcon
            icon="tdesign:send"
            type="submit"
            text="Request"
            className="team-leave-submit-button"
          />

          {showCalendar && (
            <Calendar
              selectedDates={selectedDates}
              onDateSelect={handleCalendarDateSelect}
              onClose={() => setShowCalendar(false)}
            />
          )}
        </form>
      </div>
    </div>
  );
};

export default TeamLeave;
